import {IJackpotThresholdToastPresenter, IJackpotThresholdToastPresenterEnhancedOptions, IJackpotThresholdToastPresenterExtraOptions} from "./jackpot-threshold-toast-presenter.interface";
import {TimeSpan} from "@sparkware/app-utils";
import {IServiceFactory} from "../../../service-factory.interface";
import {
    IJackpotToastThresholdsBatchConfiguration, IJackpotToastThresholdValueConfiguration
} from "../../../configurations/config-data-sections/jackpot-toaster.configuration";
import {NullableDate, NullableUndefinedString} from "../../../../../common/types/nullable-types";
import classNames from "classnames";
import {ToastStylingClassNames} from "../../../toaster/toast-helpers/toast-classnames.helper";
import {JackpotToastComponent, JackpotToastType} from "../../../../components/jackpot-toast/jackpot-toast.component";
import {KnownJackpotId} from "../../jackpot.constants";
import {TrendingRecommendationTypeEnum} from "../../../ai-recommendations-reader/ai-recommendations-reader.service.interface";
import {imagePreload} from "../../../../../common/utils/image-preload";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {onCloseAnalytics, onGameClickedAnalytics, onShowAnalytics} from "./daily-jackpot-toast-analytics..helper";

const ThresholdToastShownTimestamp = {
    [KnownJackpotId.Daily]: "cgp_dailyThresholdToastShownTimestamp",
    [KnownJackpotId.Twilight]: "cgp_twilightThresholdToastShownTimestamp",
    [KnownJackpotId.HourlyCaptain8]: "cgp_power8ThresholdToastShownTimestamp"
}

export class DailyJackpotThresholdToastPresenter implements IJackpotThresholdToastPresenter<IJackpotThresholdToastPresenterExtraOptions> {
    constructor(private services: IServiceFactory) {
        makeObservable(this);

        this.services.gameRunner.then(gameRunner => {
            reaction(() => gameRunner.currentRunningGame, (currentRunningGame) => {
                this._isGameRunning = Boolean(currentRunningGame);
            }, {
                fireImmediately: true
            })
        });

        /**
         * TODO: For DailyJackpotThresholdToastPresenter we make an instance for each recurring jackpot so we could pass the jackpot id to the constructor and
         * here we can cleanup only the relevant timestamp, instead of calling removing all of the timestamp multiple times (once for each instance)
         * Or, solution 2, reuse the same DailyJackpotThresholdToastPresenter instance for each recurring jackpot toast handler
         * Or, solution 3, extract this to a separate class and have the reaction only once
         */
        reaction(() => this.services.user.isAuthorized, () => {
            this.services.localStorage.removeItem(ThresholdToastShownTimestamp[KnownJackpotId.Daily]);
            this.services.localStorage.removeItem(ThresholdToastShownTimestamp[KnownJackpotId.Twilight]);
            this.services.localStorage.removeItem(ThresholdToastShownTimestamp[KnownJackpotId.HourlyCaptain8]);
        })
    }

    @observable.ref
    private _imagePrerequisiteLoaded = false;

    @observable.ref
    private _isGameRunning = true;

    showToast(options: IJackpotThresholdToastPresenterEnhancedOptions<IJackpotThresholdToastPresenterExtraOptions>) {
        return this._showThresholdToast(options.jackpotId, options.thresholdBatchConfiguration, options.previousAmount, options.currentAmount, options.defaultToastTitle, options.gameTypes, options.mustHitTime);
    }

    private checkTimestamp(jackpotId: KnownJackpotId) {
        const timestamp = this.services.localStorage.getItem(ThresholdToastShownTimestamp[jackpotId]);
        if (timestamp === null) {
            return false;
        }

        const date = new Date(parseInt(timestamp));
        if (date.toString() === "Invalid Date" || isNaN(date.getTime())) {
            this.services.localStorage.removeItem(ThresholdToastShownTimestamp[jackpotId]);
            return false;
        }

        const currentTime = Date.now();
        const lastToastTime = date.getTime();
        let notShowAlertForThisLong = TimeSpan.parse( "00:30:00" );
        try {
            const limitTimeSpanConfig = this.services.configuration.servicesConfigs.JackpotToaster?.limitTimespan;
            notShowAlertForThisLong = TimeSpan.parse( limitTimeSpanConfig );
        } catch (e) {
        }
        // return true if the difference is less than 30 minutes, 30 min * 60 sec in a minute * 1000 ms in a second
        if (currentTime - lastToastTime < notShowAlertForThisLong.totalMilliseconds) {
            return true;
        } else {
            this.services.localStorage.removeItem(ThresholdToastShownTimestamp[jackpotId]);
            return false;
        }
    }
    private setTimestamp(jackpotId: KnownJackpotId) {
        this.services.localStorage.setItem(
            ThresholdToastShownTimestamp[jackpotId],
            Date.now()
        );
    }

    private _showThresholdToast(jackpotId: KnownJackpotId, thresholdBatchConfig: IJackpotToastThresholdsBatchConfiguration, previousAmount: number, currentAmount: number, defaultToastTitle: NullableUndefinedString, gameTypes: number[], mustHitTime: NullableDate) {

        // Prerequisite. If any threshold toast was shown for the current jackpotId, do not show it for the next 30 minutes.
        if (this.checkTimestamp(jackpotId)) {
            return null;
        }

        // 1. Identify the threshold for which we show the toast
        const thresholds = thresholdBatchConfig.thresholds ?? [];

        if(thresholds.length === 0) {
            this.services.logger.error(`Missing threshold values configurations for jackpot toasts`);
            return null;
        }

        const thresholdValueConfig = this._matchThresholdValueConfig(thresholds, previousAmount, currentAmount);
        if(!thresholdValueConfig) {
            return null;
        }

        // 2. Identify the list of suggested games. Instead of taking the games from thresholdBatchConfig.gameTypes that comes from config and is usually an array with only one game,
        // (as it is done in JackpotThresholdToastPresenter)
        // we take the list of games from jackpot feeds that is passed here from JackpotModel
        // let gameTypes = thresholdBatchConfig.gameTypes ?? [];
        if(gameTypes.length === 0) {
            this.services.logger.error(`Missing game types for jackpot toasts thresholds`);
            return null;
        }

        // 3. Take the gameTypes currated list, pass it to AI to order it and take the first 4 for suggestions
        gameTypes = this.services.aiEngine.sortGamesByAI(gameTypes, TrendingRecommendationTypeEnum.ActiveUsers)

        //filter only the games that are in the layout
        let gamesInfo = gameTypes.map(gt => this.services.games.gamesMap[gt])
            .filter(gameInfo => Boolean(gameInfo));

        if(gamesInfo.length === 0) {
            this.services.logger.warning(`None of the following game types ${gameTypes.join(",")} configured for jackpot toasts thresholds are in the layout` );
            return null;
        }

        gamesInfo = gamesInfo.filter(gInfo => gInfo.restriction.isGameAllowedToPlayReal())

        // take the first 4 elements, from index 0 (inclusive) to index 4 (exclusive)
        gamesInfo = gamesInfo.slice(0, 4);

        /* For the Logo, we need to map from jackpotId to logo */
        /* In widget, the jackpot type (that defines the images and styles) is defined in admin, however, here we don't have that information */
        const jpIdToLogoMap = {
            [KnownJackpotId.Daily]: this.services.theme.currentTheme.assets.images.jackpot_toast.daily_jp_toast_logo_png.toString(),
            [KnownJackpotId.Twilight]: this.services.theme.currentTheme.assets.images.jackpot_toast.twilight_jp_toast_logo_png.toString(),
            [KnownJackpotId.HourlyCaptain8]: this.services.theme.currentTheme.assets.images.jackpot_toast.power8_jp_toast_logo_png.toString()
        }
        const logoImage: string = jpIdToLogoMap[jackpotId];

        imagePreload(logoImage).finally(() => {
            runInAction(() => {
                this._imagePrerequisiteLoaded = true;
            });
        });

        let mainTitle = this.services.translation.translate("jackpot_threshold_toast_main_title");
        let secondTitle = this.services.translation.translate("jackpot_threshold_toast_second_title");
        let message = this.services.translation.translate("jackpot_toast_suggested_games_text");

        return this.services.toaster.showCustomToast({
            id: thresholdValueConfig.toasterName,
            toastOptions: {
                timeout: TimeSpan.fromSeconds(15),
                className: classNames(
                    ToastStylingClassNames.ResetStyles,
                    ToastStylingClassNames.FrostedGlassBg,
                    ToastStylingClassNames.WithPadding,
                    ToastStylingClassNames.WithBorderLight,
                    ToastStylingClassNames.WithShadow,
                    "orbit-toast-jackpot"
                ),
            },
            renderContent: (handler) => <JackpotToastComponent
                handler={handler}
                toastType={JackpotToastType.Threshold}
                amount={currentAmount}
                mustHitTime={mustHitTime}
                mainTitle={mainTitle}
                secondTitle={secondTitle}
                message={message}
                logoImage={logoImage}
                gamesInfo={gamesInfo}
                onGameClickAnalytics={(gameType: number, gamePosition: number) => {
                    onGameClickedAnalytics(this.services, thresholdValueConfig.toasterName, JackpotToastType.Threshold, currentAmount, gameType, gamePosition);
                }}
            />,
            onToastShown: () => {
                this.setTimestamp(jackpotId);
                onShowAnalytics(this.services, thresholdValueConfig.toasterName, JackpotToastType.Threshold, currentAmount);
            },
            onToastClosed: (closeReason) => {
                onCloseAnalytics(this.services, thresholdValueConfig.toasterName, JackpotToastType.Threshold, currentAmount, closeReason);
            },
            displayCondition: () => {
                return this._imagePrerequisiteLoaded && !this._isGameRunning && !this.services.dialog.hasOpenDialogs
            }
        });
    }

    protected _getGenericToastImage(): string {
        return this.services.theme.currentTheme.assets.images.jackpot_widget.crown_png.toString();
    }

    private _matchThresholdValueConfig(thresholdsConfigs: IJackpotToastThresholdValueConfiguration[], previousJackpotAmount: number, currentJackpotAmount: number): IJackpotToastThresholdValueConfiguration | null {
        for (let thresholdValueConfig of thresholdsConfigs) {
            if(currentJackpotAmount > thresholdValueConfig.threshold && previousJackpotAmount <= thresholdValueConfig.threshold) {
                return thresholdValueConfig;
            }
        }

        return null;
    }


}